/**
 * Deployment configuration.
 *
 * The site key (`sw_site_live_…`) is public and belongs in the page — it only
 * attributes telemetry to this site so console Sentinel can see the events.
 * The secret key (`sw_secret_live_…`) is never here: the Worker holds it and
 * performs the `/api/v1/siteverify` redeem server-side.
 */

/**
 * Build-time site key. Used for local development and as the fallback when
 * `/api/config` cannot be reached. In the deployed app the key comes from the
 * API instead, so rotating it is a portal edit rather than a rebuild.
 */
export const SITE_KEY = import.meta.env.VITE_SHIELDWALL_SITE_KEY ?? "";

export interface RuntimeConfig {
  siteKey: string;
  /** Non-fatal configuration problem to surface in the slot. */
  warning: string | null;
}

/**
 * Reads the site key from the Static Web App's server-side settings.
 *
 * SWA environment variables reach the managed Functions, never the bundle, so
 * a key set in the portal is invisible to the browser until something serves
 * it. One same-origin request, before the widget mounts, so the widget is
 * never mounted twice and never burns two challenges.
 */
export async function fetchRuntimeConfig(): Promise<RuntimeConfig> {
  const res = await fetch("/api/config", { headers: { accept: "application/json" } });
  if (!res.ok) throw new Error(`config fetch failed: ${res.status}`);
  const body = (await res.json()) as Partial<RuntimeConfig>;
  return { siteKey: body.siteKey ?? "", warning: body.warning ?? null };
}

/** Captcha API base. The widget appends /challenge and /verify. */
export const API_BASE =
  import.meta.env.VITE_SHIELDWALL_API ?? "https://api.botproof.app/api/captcha";

/**
 * Action profile. The server scores per action, so keep this stable — changing
 * it mid-measurement makes the before and after incomparable.
 */
export const ACTION = "form_submit";

/**
 * responseToken TTL is ~2 minutes server-side. Re-arm a little early so a token
 * cannot expire in flight between the submit click and the Worker redeem.
 */
export const TOKEN_TTL_MS = 90_000;

/**
 * Missing key is a soft warning: the widget still runs anonymous challenges
 * against the live API. Put a public `sw_site_live_…` key here for Sentinel
 * telemetry — never a `sw_secret_live_…` Worker secret.
 */
export const CONFIG_ERROR = SITE_KEY
  ? null
  : "VITE_SHIELDWALL_SITE_KEY is not set. Anonymous challenges still run; add a public sw_site_live_… key from the botproof console for telemetry.";
